/**
 * GenerationPanel - Form for generating new images from text prompts.
 *
 * Layout: prompt textarea spans the full width above a three-column footer
 * (left: Aspect Ratio + Resolution stacked; middle: References (Optional);
 * right: Generate button). The image preview lives above this panel in the
 * page-level grid, not inside it.
 */
import { useCallback, useEffect } from 'react';
import { useCreativeStudioStore, useGenerationForm } from '@/store/creativeStudioStore';
import {
  useGenerateImage,
  useGenerationResult,
} from '@/hooks/queries/useCreativeStudioQueries';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { AISparkleIcon } from '@/components/ui/Icons';
import { AspectRatioSelector } from './AspectRatioSelector';
import { ResolutionSelector } from './ResolutionSelector';
import { ReferenceImagePicker } from './ReferenceImagePicker';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import '@/styles/tooltip.css';

const MAX_PROMPT_LENGTH = 2000;
const MIN_PROMPT_LENGTH = 10;

export function GenerationPanel() {
  const { prompt, aspectRatio, resolution, referenceImages, enableGrounding } =
    useGenerationForm();

  const {
    setPrompt,
    setAspectRatio,
    setResolution,
    setEnableGrounding,
    startGeneration,
    completeGeneration,
    failGeneration,
    currentJobId,
    isGenerating,
  } = useCreativeStudioStore();

  const generateMutation = useGenerateImage();
  const { data: generationResult } = useGenerationResult(
    currentJobId,
    isGenerating && currentJobId !== null
  );

  useEffect(() => {
    if (generationResult) {
      if (generationResult.status === 'completed') {
        completeGeneration(generationResult);
      } else if (generationResult.status === 'failed') {
        failGeneration(generationResult.error_message || 'Generation failed');
      }
    }
  }, [generationResult, completeGeneration, failGeneration]);

  const handleGenerate = useCallback(async () => {
    if (prompt.length < MIN_PROMPT_LENGTH) {
      return;
    }

    try {
      const result = await generateMutation.mutateAsync({
        prompt,
        aspect_ratio: aspectRatio,
        resolution,
        // Send the store's name explicitly so the backend honors the
        // labels the user is actually seeing in the thumbnails. After a
        // removal the store's "next free slot" algorithm can produce a
        // non-monotonic array (e.g. [ref-image-2, ref-image-1]); sending
        // `null` would make the backend renumber by array index and flip
        // those labels, breaking any prompt that references them by name.
        reference_images:
          referenceImages.length > 0
            ? referenceImages.map((r) => ({ media_image_id: r.id, name: r.name }))
            : undefined,
        enable_grounding: enableGrounding,
      });

      startGeneration(result.job_id);
    } catch (error) {
      console.error('Failed to start generation:', error);
      failGeneration(error instanceof Error ? error.message : 'Failed to start generation');
    }
  }, [
    prompt,
    aspectRatio,
    resolution,
    referenceImages,
    enableGrounding,
    generateMutation,
    startGeneration,
    failGeneration,
  ]);

  const isValidPrompt = prompt.length >= MIN_PROMPT_LENGTH && prompt.length <= MAX_PROMPT_LENGTH;
  const canGenerate = isValidPrompt && !isGenerating;

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Image</h2>

      {/* Prompt — full width */}
      <div className="mb-4">
        <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
          Describe your ad image
        </label>
        <textarea
          id="prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A professional product photo of a luxury watch on marble background with dramatic lighting. The headline reads 'TIMELESS ELEGANCE' in elegant serif font..."
          rows={5}
          maxLength={MAX_PROMPT_LENGTH}
          disabled={isGenerating}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
                     focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                     disabled:bg-gray-100 disabled:text-gray-500
                     placeholder:text-gray-400 resize-none"
        />
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <span>
            {prompt.length < MIN_PROMPT_LENGTH && prompt.length > 0 && (
              <span className="text-amber-600">
                Minimum {MIN_PROMPT_LENGTH} characters required
              </span>
            )}
          </span>
          <span
            className={prompt.length > MAX_PROMPT_LENGTH * 0.9 ? 'text-amber-600' : ''}
          >
            {prompt.length} / {MAX_PROMPT_LENGTH}
          </span>
        </div>
      </div>

      {/* Two-column footer above a full-width Generate button.
          Columns: 6/6 so the option fields get all the horizontal space.
          The Refine tab mirrors this layout. */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left: Aspect ratio + Resolution stacked vertically */}
        <div className="col-span-12 md:col-span-6 space-y-3">
          <AspectRatioSelector
            value={aspectRatio}
            onChange={setAspectRatio}
            disabled={isGenerating}
          />
          <ResolutionSelector
            value={resolution}
            onChange={setResolution}
            disabled={isGenerating}
          />
        </div>

        {/* Right: References */}
        <div className="col-span-12 md:col-span-6">
          <div className="flex items-center gap-1 mb-2">
            <label className="block text-sm font-medium text-gray-700">
              References ({referenceImages.length}/14)
            </label>
            <div className="tooltip-container">
              <button
                type="button"
                aria-label="About references"
                aria-describedby="references-hint-tooltip"
                className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
              >
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </button>
              <span
                id="references-hint-tooltip"
                role="tooltip"
                className="tooltip-text tooltip-text-left"
                style={{ whiteSpace: 'normal', width: '20rem', maxWidth: '60vw', textAlign: 'left' }}
              >
                Add reference images from your Media Library to guide the generation. Then, reference the name of the image (under its thumbnail) in your prompt.
              </span>
            </div>
          </div>
          <ReferenceImagePicker disabled={isGenerating} />
        </div>
      </div>

      {/* Full-width Generate button below the two-column row */}
      <div className="mt-4">
        <Button
          variant="primary"
          size="md"
          onClick={handleGenerate}
          disabled={!canGenerate}
          mobileTouch
          className="w-full"
        >
          <AISparkleIcon className="w-6 h-6 mr-1.5 ml-0.5 -mt-1 flex-shrink-0" />
          {isGenerating ? 'Generating…' : 'Generate'}
        </Button>
      </div>

      {/* Grounding toggle (low-frequency control) below the footer */}
      <div className="mt-4 flex items-center gap-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enableGrounding}
            onChange={(e) => setEnableGrounding(e.target.checked)}
            disabled={isGenerating}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">
            Enable Google Search grounding
          </span>
        </label>
        <div className="tooltip-container">
          <button
            type="button"
            aria-label="About Google Search grounding"
            aria-describedby="grounding-hint-tooltip"
            className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
          >
            <InformationCircleIcon className="h-4 w-4 text-gray-400" />
          </button>
          <span
            id="grounding-hint-tooltip"
            role="tooltip"
            className="tooltip-text tooltip-text-left"
            style={{ whiteSpace: 'normal', width: '20rem', maxWidth: '60vw', textAlign: 'left' }}
          >
            Lets Gemini pull in real-time information (e.g., weather, news, current events) while generating your image.
          </span>
        </div>
      </div>

      {/* Always-mounted status row so the Generate button doesn't move
          when the spinner appears. `invisible` keeps the element in the
          layout flow but hides its paint, eliminating the click-time
          height jump. */}
      <div
        className={`mt-4 text-center text-sm text-gray-600 ${isGenerating ? 'visible' : 'invisible'}`}
        aria-hidden={!isGenerating}
      >
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full" />
          <span>Generating your image... This may take 10-30 seconds.</span>
        </div>
      </div>
    </Card>
  );
}
